<!--
  Site listings container component
  
  2025-05-27: Added hover event handling to communicate with map for marker highlighting
-->
<template>
  <v-sheet @scroll="!isMobile && handleDeskTopScroll($event)" class="listings">
    <div v-if="title" class="section-header mb-10 mb-md-16">
      <h2 class="section-title text-center mb-2" v-text="title" />
      <div class="section-accent mx-auto mb-4"></div>
    </div>
    <v-row v-if="listings.length > 0" class="justify-start ma-0">
      <v-col
        v-for="(item, index) in listings"
        :key="item.id"
        :cols="cols"
        :sm="sm"
        :md="md"
        :lg="lg"
        :xl="xl"
      >
        <site-listings-item
          :item="item"
          :index="index"
          :horizontal="horizontal"
          :carousel-lg="carouselLg"
          :show-calculations="showCalculations"
          @listing-hover="handleListingHover"
        />
      </v-col>
    </v-row>
    <v-row
      v-else-if="loading"
      class="justify-center align-center fill-height ma-0"
      style="min-height: calc(100vh - 300px)"
    >
      <v-col cols="12" class="text-center">
        <v-progress-circular
          :color="actionsColor"
          indeterminate
          size="32"
        ></v-progress-circular>
      </v-col>
    </v-row>
    <v-row
      v-else
      class="justify-center align-center fill-height ma-0"
      style="min-height: calc(100vh - 300px)"
    >
      <v-col cols="12" sm="8" md="6" class="text-center">
        <v-sheet class="pa-6 rounded-lg" elevation="0">
          <v-icon size="64" class="mb-4" :color="actionsColor"
            >mdi-home-search-outline</v-icon
          >
          <h3 class="text-h6 font-weight-medium primary--text mb-2">
            No matches found
          </h3>
          <p class="text-body-2 grey--text text--darken-1">
            Try adjusting your filters to see more options
          </p>
        </v-sheet>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import SiteListingsItem from "@/components/home/site-listings-item.vue";
import DeviceMixin from "@/mixins/device-mixin";
import stylesMixin from "@/mixins/styles-mixin";

export default {
  name: "site-listings",
  components: { SiteListingsItem },
  mixins: [DeviceMixin, stylesMixin],

  props: {
    title: String,
    horizontal: {
      type: Boolean,
      default: false,
    },
    featured: {
      type: Boolean,
      default: false,
    },
    cols: {
      type: [Number, String],
      default: 12,
    },
    sm: {
      type: [Number, String],
      default: 6,
    },
    md: {
      type: [Number, String],
      default: 4,
    },
    lg: {
      type: [Number, String],
      default: null,
    },
    xl: {
      type: Boolean,
      default: null,
    },
    carouselLg: {
      type: Number,
      default: 12,
    },
    showCalculations: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { isLoading: false, page: 1 };
  },
  computed: {
    ...mapState({
      listings: (state) => state.listingsModule.listings,
      pagination: (state) => state.listingsModule.pagination,
      loading: (state) => state.listingsModule.loading,
    }),
  },
  beforeMount() {
    this.setListings([]);
  },
  mounted() {
    this.getListings(this.featured ? { featured: true } : this.$route.query);
    if (!this.isMobile) return;

    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("touchmove", this.handleScroll);
  },
  beforeDestroy() {
    if (!this.isMobile) return;

    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("touchmove", this.handleScroll);
  },
  methods: {
    ...mapActions("listingsModule", ["getListings"]),
    ...mapMutations("listingsModule", ["setListings", "setHoveredListingId"]),
    async handleDeskTopScroll({
      target: { scrollHeight, clientHeight, scrollTop },
    }) {
      const { page, per_page, count } = this.pagination;
      const hasNext = page * per_page < count;

      const scrollPosition = scrollTop + clientHeight;
      const threshold = scrollHeight * 0.8;

      if (hasNext && !this.isLoading && scrollPosition >= threshold) {
        this.getNextPage();
      }
    },
    async handleScroll() {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;

      const scrollPosition = scrollTop + clientHeight;
      const threshold = scrollHeight * 0.8;

      const { page, per_page, count } = this.pagination;
      const hasNext = page * per_page < count;

      if (hasNext && !this.isLoading && scrollPosition >= threshold) {
        this.getNextPage();
      }
    },
    getNextPage() {
      this.isLoading = true;
      this.page += 1;
      this.getListings({
        ...this.$route.query,
        page: this.page,
        append: true,
      });
    },
    handleListingHover(listingId) {
      this.setHoveredListingId(listingId);
    },
  },
  watch: {
    "$route.query"() {
      this.getListings(this.featured ? { featured: true } : this.$route.query);
    },
    pagination(val, oldVal) {
      if (val.page > oldVal.page) this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.section-header {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}
.section-title {
  font-family: "Fraunces", serif;
  font-size: 2.2rem;
  font-weight: 700;
  letter-spacing: -0.5px;
}
.section-accent {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #1976d2 0%, #42a5f5 100%);
  border-radius: 2px;
}
</style>
